import { Zap, ShieldCheck, MapPin, Bell } from 'lucide-react'
import { useParallax } from '../../hooks/useParallax.js'
import Button from '../ui/Button.jsx'
import logo from '../../assets/decida_logo_dark.png'

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

const BADGES = [
  { icon: Zap, label: 'Responses in minutes' },
  { icon: ShieldCheck, label: 'Verified merchants only' },
  { icon: MapPin, label: 'Accra, launching now' },
]

/* ── Live mission feed mock data ─────────────────────────────── */
const MOCK_MISSIONS = [
  {
    id: 1,
    category: 'Phone Repair',
    request: 'Screen replacement, iPhone 13',
    budget: 'GHS 180–250',
    area: 'East Legon',
    urgency: 'Now',
    urgencyColor: 'bg-red-100 text-red-600',
    dot: 'bg-red-400',
    responses: 3,
  },
  {
    id: 2,
    category: 'Beauty & Hair',
    request: 'Box braids, medium length',
    budget: 'GHS 120–200',
    area: 'Osu',
    urgency: 'Today',
    urgencyColor: 'bg-amber-100 text-amber-600',
    dot: 'bg-amber-400',
    responses: 2,
  },
  {
    id: 3,
    category: 'Electrician',
    request: 'Fix faulty circuit breaker',
    budget: 'GHS 80–150',
    area: 'Cantonments',
    urgency: 'Flexible',
    urgencyColor: 'bg-primary-light text-primary',
    dot: 'bg-primary',
    responses: 4,
  },
]

/* ── Stat pill ───────────────────────────────────────────────── */
function StatPill({ value, label }) {
  return (
    <div className="flex flex-col items-center px-3 py-3 sm:px-5 rounded-2xl bg-white/80
                    backdrop-blur-sm border border-gray-100 shadow-sm">
      <span className="text-xl sm:text-2xl font-extrabold text-brand-dark leading-none">{value}</span>
      <span className="text-[10px] sm:text-[11px] font-medium text-brand-gray mt-0.5 text-center leading-tight">{label}</span>
    </div>
  )
}

/* ── Mission card mock ───────────────────────────────────────── */
function MissionCard({ mission, style }) {
  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-card p-4
                 transition-all duration-300 hover:-translate-y-1 hover:shadow-card-md"
      style={style}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <span className="text-[11px] font-bold tracking-widest uppercase text-primary">
          {mission.category}
        </span>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold
                          px-2 py-0.5 rounded-full flex-shrink-0 ${mission.urgencyColor}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${mission.dot}`} />
          {mission.urgency}
        </span>
      </div>
      <p className="text-sm font-semibold text-brand-dark mb-2 leading-snug">{mission.request}</p>
      <div className="flex items-center gap-3 text-xs text-brand-gray">
        <span className="flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2"
            viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {mission.area}
        </span>
        <span>{mission.budget}</span>
        <span className="ml-auto flex items-center gap-1 text-primary font-semibold">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2"
            viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {mission.responses} offers
        </span>
      </div>
    </div>
  )
}

export default function Hero() {
  const orb1Ref = useParallax({ speed: 0.25 })
  const orb2Ref = useParallax({ speed: -0.18 })
  const panelRef = useParallax({ speed: 0.1 })

  return (
    <section
      id="top"
      className="relative bg-white overflow-hidden lg:min-h-screen lg:flex lg:items-center"
    >
      {/* ── Parallax ambient background ───────────────────── */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          ref={orb1Ref}
          className="parallax-layer glow-orb absolute -top-40 -right-40
                     w-[640px] h-[640px] rounded-full
                     bg-gradient-to-br from-primary/10 to-primary/3
                     blur-[120px]"
        />
        <div
          ref={orb2Ref}
          className="parallax-layer glow-orb absolute -bottom-32 -left-32
                     w-[480px] h-[480px] rounded-full
                     bg-gradient-to-tr from-accent/8 to-transparent
                     blur-[96px]"
          style={{ animationDelay: '2s' }}
        />
        <div
          className="absolute inset-0 opacity-[0.022]"
          style={{
            backgroundImage: 'radial-gradient(circle, #0E7C86 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-primary/[0.02] to-accent/[0.03]" />
      </div>

      {/* ── Two-column layout ──────────────────────────────── */}
      <div className="section-container relative z-10 pt-24 pb-10 md:pt-28 md:pb-20 lg:pt-36 lg:pb-24 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* ── Left: copy ──────────────────────────────────── */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">


            {/* Eyebrow */}
            <p className="eyebrow mb-4 hero-eyebrow justify-center lg:justify-start">
              <span className="w-6 h-px bg-primary" />
              Now live in Accra
            </p>

            {/* Headline */}
            <h1
              className="hero-headline font-extrabold text-brand-dark mb-4"
              style={{ fontSize: 'clamp(2.1rem, 5vw, 4rem)', lineHeight: 1.08 }}
            >
              Stop asking around.{' '}
              <br className="hidden sm:block" />
              <span className="text-primary">Ask DecidA.</span>
            </h1>

            {/* Sub-headline */}
            <p className="hero-sub text-base text-brand-gray leading-relaxed max-w-md mb-5">
              Submit one request. <br />
              Get offers from verified providers nearby.
            </p>

            {/* Trust badges */}
            <div className="hero-badges flex flex-wrap justify-center lg:justify-start gap-2 mb-6" role="list">
              {BADGES.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  role="listitem"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold
                             text-primary bg-primary-light border border-primary/15
                             px-3 py-1.5 rounded-full
                             transition-all duration-200 hover:border-primary/40
                             hover:shadow-sm hover:-translate-y-px cursor-default"
                >
                  <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                  {label}
                </span>
              ))}
            </div>

            {/* CTAs — full-width on mobile, side-by-side on sm+ */}
            <div className="hero-cta w-full flex flex-col sm:flex-row sm:justify-center lg:justify-start gap-3 mb-7">
              <Button
                variant="primary"
                className="btn-shimmer w-full sm:w-auto !px-8 !py-4 !text-[15px] !min-h-[52px]
                           hover:scale-[1.03] active:scale-[0.98]"
                onClick={() => scrollTo('waitlist')}
              >
                <Bell className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                Join the Waitlist
              </Button>
              {/* <Button
                variant="outline"
                className="w-full sm:w-auto !px-8 !py-4 !text-[15px] !min-h-[52px]
                           hover:scale-[1.03] active:scale-[0.98]"
                onClick={() => scrollTo('join')}
              >
                Join as Merchant
              </Button> */}
            </div>

            {/* Stats row — 3-column grid on mobile */}
            <div
              className="hero-cta w-full grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:justify-center lg:justify-start sm:gap-3"
              style={{ animationDelay: '0.72s' }}
            >
              <StatPill value="3–5" label="Per request" />
              <StatPill value="<1hr" label="Urgent jobs" />
              <StatPill value="100%" label="Free to join" />
            </div>
          </div>

          {/* ── Right: live mission panel (desktop only) ─────── */}
          <div
            ref={panelRef}
            className="parallax-layer hidden lg:flex flex-col gap-4"
            style={{ animationDelay: '0.3s' }}
          >
            {/* Panel header */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full
                                   rounded-full bg-primary opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
                </span>
                <span className="text-xs font-bold text-brand-dark">Live Requests · Accra</span>
              </div>
              <span className="text-[11px] text-brand-gray font-medium">Updated just now</span>
            </div>

            {/* Mission cards — staggered float */}
            {MOCK_MISSIONS.map((m, i) => (
              <MissionCard
                key={m.id}
                mission={m}
                style={{ animationDelay: `${i * 0.12}s` }}
              />
            ))}

            {/* Bottom CTA teaser */}
            <div className="rounded-2xl bg-gradient-to-r from-primary to-primary-dark
                            p-5 text-white text-center shadow-card-md">
              <p className="text-xs font-bold tracking-widest uppercase text-white/60 mb-1">
                Merchants in Accra
              </p>
              <p className="text-sm font-semibold mb-3">
                These requests are waiting for a provider like you.
              </p>
              <button
                onClick={() => scrollTo('join')}
                className="inline-flex items-center gap-1.5 text-xs font-bold
                           bg-white text-primary px-4 py-2 rounded-lg
                           transition-all duration-200 hover:scale-[1.03] hover:shadow-md"
              >
                Register your business →
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>
  )
}
